import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Box, Text, Button, SimpleGrid, Card, Group, Badge } from '@mantine/core'
import { format } from 'date-fns'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { TagFilterChips, AuthGateCta } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { TagCategory } from '../../app/models/tag'
import BuildCardThumbnail from './BuildCardThumbnail'

export default observer(function BuildsDashboard () {
  const { buildStore, userStore } = useStore()
  const { loadBuilds, buildsByDate, loadingInitial } = buildStore
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const firearmModelId = params.get('firearmModelId') || undefined
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  useEffect(() => {
    loadBuilds(firearmModelId, undefined, selectedTagIds.length ? { tagIds: selectedTagIds, tagMatch: 'any' } : undefined)
  }, [loadBuilds, firearmModelId])

  const handleTagToggle = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    setSelectedTagIds(next)
    loadBuilds(firearmModelId, undefined, next.length ? { tagIds: next, tagMatch: 'any' } : undefined)
  }

  if (loadingInitial) {
    return <LoadingComponent content="Loading builds..." />
  }

  if (buildsByDate.length === 0) {
    return (
      <Box>
        <Box mb="xl">
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 4 }}>
            COMMUNITY BUILDS
          </Text>
          <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
            Public build configurations — parts lists attached to firearm models.
          </Text>
        </Box>
        <Box
          py="xl"
          px="lg"
          sx={{
            textAlign: 'center',
            background: 'var(--gs-surface)',
            borderRadius: 8,
            border: '1px solid var(--gs-border)'
          }}
        >
          <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: 'var(--gs-muted)', marginBottom: 16 }}>
            No builds yet.
            {firearmModelId && ' Try removing the filter or create a build for this model.'}
          </Text>
          {userStore.isLoggedIn
            ? (
            <Button component={Link} to="/builds/new" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
              Create a build
            </Button>
              )
            : (
            <AuthGateCta
              variant="inline"
              action="document your first build"
              pitch="Create a free account to log your build, attach a parts list, and share it with the community."
            />
              )}
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <Box>
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 4 }}>
            COMMUNITY BUILDS
          </Text>
          <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
            {firearmModelId ? 'Builds for this model' : 'Public build configurations'}
          </Text>
        </Box>
        {userStore.isLoggedIn && (
          <Button component={Link} to="/builds/new" size="sm" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
            Create build
          </Button>
        )}
      </Box>
      {!userStore.isLoggedIn && (
        <Box mb="md">
          <AuthGateCta
            variant="banner"
            action="log your own builds"
            pitch="Create a free account to document your builds, attach parts lists, and earn AmmoScore from the community."
          />
        </Box>
      )}
      <TagFilterChips
        selectedTagIds={selectedTagIds}
        onToggleTag={handleTagToggle}
        categories={[TagCategory.Platform, TagCategory.Caliber]}
      />
      <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
        {buildsByDate.map((build) => (
          <Card
            key={build.id}
            component={Link}
            to={`/builds/${build.id}`}
            padding={0}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8,
              overflow: 'hidden',
              transition: 'border-color 150ms',
              '&:hover': { borderColor: 'var(--gs-border-bright)' }
            }}
          >
            <BuildCardThumbnail url={build.mainPhotoUrl} alt={build.name} variant="full" />
            <Box p="md">
              <Text
                weight={600}
                size="md"
                lineClamp={1}
                mb={2}
                sx={{ fontFamily: 'Oswald, sans-serif', color: 'var(--gs-text)' }}
              >
                {build.name}
              </Text>
              <Text size="xs" mb={6} sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                {build.firearmModelName}
              </Text>
              {(build.tags?.length ?? 0) > 0 && (
                <Group spacing={4} mb={6}>
                  {build.tags!.slice(0, 3).map((t) => (
                    <Badge
                      key={t.id}
                      size="xs"
                      variant="outline"
                      radius="sm"
                      sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {t.displayName || t.name}
                    </Badge>
                  ))}
                  {(build.tags!.length ?? 0) > 3 && (
                    <Badge size="xs" variant="outline" radius="sm" sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-muted)' }}>
                      +{build.tags!.length! - 3}
                    </Badge>
                  )}
                </Group>
              )}
              {build.description && (
                <Text size="xs" lineClamp={2} mb={8} sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                  {build.description}
                </Text>
              )}
              <Group position="apart" mt={8}>
                <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                  by {build.ownerUsername}
                </Text>
                <Group spacing={8}>
                  <Text
                    size="xs"
                    sx={{ fontFamily: "'DM Mono', monospace", color: 'var(--gs-accent)', fontWeight: 600 }}
                  >
                    {build.parts?.length ?? 0} parts
                  </Text>
                  {build.voteScore !== 0 && (
                    <Text size="xs" sx={{ fontFamily: "'DM Mono', monospace", color: 'var(--gs-accent)' }}>
                      ▲{build.voteScore}
                    </Text>
                  )}
                </Group>
              </Group>
              <Text size="xs" mt={2} sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', opacity: 0.6 }}>
                {format(new Date(build.updatedAt), 'MMM d, yyyy')}
              </Text>
            </Box>
          </Card>
        ))}
      </SimpleGrid>
    </Box>
  )
})
